import Raven from 'raven-js';
import getUserInfo from 'hub-http/userInfo';
import { useEffect, useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { H6 } from 'foundations-components/transitional/components/H6';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { SelectableButton } from 'foundations-components/transitional/components/SelectableButton';
import { getIsFreePortal } from 'onboarding-scopes/monetization-utils';
import { TALK_TO_SALES_SUB_MODALS } from '../../../constants/talkToSales';
import { loadAndOpenSalesChat } from '../../../utils/loadAndOpenSalesChat';
import { getRepName } from '../../../utils/pql';
import { getTrackingProperties, track } from '../../../utils/usageTracker';
import { EmailModal } from './EmailModal';
import { GetACallModal } from './GetACallModal';
import { MeetingModal } from './MeetingModal';
import { submitPql } from '../../../utils/pql';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const AssignableModal = ({
  apiName,
  repInfo,
  trackingData,
  upgradeConfig,
  onClose
}) => {
  const [currentSubModal, setCurrentSubModal] = useState();
  const [loading, setLoading] = useState(true);
  const [isFreePortal, setIsFreePortal] = useState();
  const trackingProperties = useMemo(() => getTrackingProperties({
    apiName,
    trackingData,
    upgradeConfig,
    sourceKey: 'TALK_TO_SALES'
  }), [apiName, trackingData, upgradeConfig]);
  const hideSubModal = () => setCurrentSubModal(undefined);
  const showEmailModal = () => {
    track('communication-methods', Object.assign({
      action: 'clicked send email'
    }, trackingProperties, {
      repName: getRepName(repInfo)
    }));
    setCurrentSubModal(TALK_TO_SALES_SUB_MODALS.EMAIL);
  };
  const showGetACallModal = () => {
    track('communication-methods', Object.assign({
      action: 'clicked contact sales with phone call'
    }, trackingProperties, {
      repName: getRepName(repInfo)
    }));
    setCurrentSubModal(TALK_TO_SALES_SUB_MODALS.GET_A_CALL);
  };
  const showMeetingModal = () => {
    track('communication-methods', Object.assign({
      action: 'clicked contact sales with meeting'
    }, trackingProperties, {
      repName: getRepName(repInfo)
    }));
    setCurrentSubModal(TALK_TO_SALES_SUB_MODALS.MEETING);
  };
  const showChat = () => {
    loadAndOpenSalesChat({
      apiName,
      trackingData,
      isRetail: false
    });
    track('communication-methods', Object.assign({
      action: 'clicked chat with us'
    }, trackingProperties, {
      repName: getRepName(repInfo)
    }));
    onClose();
  };
  useEffect(() => {
    void getUserInfo().then(userInfo => {
      setIsFreePortal(getIsFreePortal(userInfo.user.scopes));
    }).catch(e => {
      setIsFreePortal(true);
      Raven.captureException(new Error('Communication methods modal opened without setting data', {
        cause: e
      }));
    }).finally(() => {
      setLoading(false);
    });
  }, []);
  useEffect(() => {
    track('communication-methods', Object.assign({
      action: 'viewed'
    }, trackingProperties, {
      repName: getRepName(repInfo)
    }));
    void submitPql({
      apiName,
      isRetail: false,
      isAssignable: true,
      repInfo,
      trackingData
    });
    //trackTalkToSalesPql(upgradeData.app);
  }, [apiName, repInfo, trackingData, trackingProperties]);
  if (loading) {
    return null;
  }
  if (currentSubModal) {
    switch (currentSubModal) {
      case TALK_TO_SALES_SUB_MODALS.EMAIL:
        return /*#__PURE__*/_jsx(EmailModal, {
          apiName: apiName,
          repInfo: repInfo,
          trackingData: trackingData,
          upgradeConfig: upgradeConfig,
          onClose: onClose
        });
      case TALK_TO_SALES_SUB_MODALS.GET_A_CALL:
        return /*#__PURE__*/_jsx(GetACallModal, {
          apiName: apiName,
          trackingData: trackingData,
          onBackClick: hideSubModal,
          onClose: onClose,
          repInfo: repInfo,
          upgradeConfig: upgradeConfig
        });
      case TALK_TO_SALES_SUB_MODALS.MEETING:
        return /*#__PURE__*/_jsx(MeetingModal, {
          apiName: apiName,
          repInfo: repInfo,
          trackingData: trackingData,
          upgradeConfig: upgradeConfig,
          onClose: onClose
        });
      default: //no-op
    }
  }
  return /*#__PURE__*/_jsxs(Modal, {
    use: "conversational",
    width: 850,
    "data-test-id": "assignable-modal",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      marginBlockEnd: "800",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "purchaseMotions.talkToSales.communicationMethods.title"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: () => onClose()
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx("div", {
        "data-test-id": "assignable-modal-body",
        children: /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsxs(Flex, {
            justifyContent: "space-between",
            gap: "400",
            children: [/*#__PURE__*/_jsxs(SelectableButton, {
              "data-test-id": "communication-method-meeting",
              size: "large",
              inlineSize: "100%",
              onClick: showMeetingModal,
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "purchaseMotions.talkToSales.communicationMethods.meeting"
              }),
              children: [/*#__PURE__*/_jsx(Badge, {
                marginBlockEnd: "200",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "purchaseMotions.talkToSales.communicationMethods.recommended"
                })
              }), /*#__PURE__*/_jsx(Illustration, {
                name: "meetings-small",
                height: 75
              })]
            }), /*#__PURE__*/_jsx(SelectableButton, {
              "data-test-id": "communication-method-call",
              size: "large",
              inlineSize: "100%",
              onClick: showGetACallModal,
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "purchaseMotions.talkToSales.communicationMethods.call"
              }),
              children: /*#__PURE__*/_jsx(Illustration, {
                name: "calling-small",
                height: 75,
                marginBlockStart: "700"
              })
            }), /*#__PURE__*/_jsx(SelectableButton, {
              "data-test-id": "communication-method-email",
              size: "large",
              inlineSize: "100%",
              onClick: showEmailModal,
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "purchaseMotions.talkToSales.communicationMethods.email"
              }),
              children: /*#__PURE__*/_jsx(Illustration, {
                name: "successfully-connected-email",
                height: 75,
                marginBlockStart: "700"
              })
            }), isFreePortal && /*#__PURE__*/_jsx(SelectableButton, {
              "data-test-id": "communication-method-chat",
              size: "large",
              inlineSize: "100%",
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "purchaseMotions.talkToSales.communicationMethods.chatWithUs"
              }),
              onClick: showChat,
              children: /*#__PURE__*/_jsx(Illustration, {
                name: "contacts-chat",
                height: 75,
                marginBlockStart: "700"
              })
            })]
          }), /*#__PURE__*/_jsx(View, {
            marginBlockStart: "900",
            children: /*#__PURE__*/_jsxs(Text, {
              textAlign: "start",
              children: [/*#__PURE__*/_jsx(H6, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "purchaseMotions.talkToSales.communicationMethods.footer.header"
                })
              }), /*#__PURE__*/_jsx("p", {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "purchaseMotions.talkToSales.communicationMethods.footer.body"
                })
              })]
            })
          })]
        })
      })
    })]
  });
};